/**
 * WHEN MAY THE EXPAND PANEL RE-SEED ITS DRAFT FROM THE ROW?
 *
 * The Areas-to-Fix panel holds the reviewer's edits in local draft state and commits them on Save.
 * That draft must be re-seeded when the SERVER hands the panel different values — a reload, a
 * re-extract, a save by someone else — and must NOT be re-seeded from the panel's own optimistic
 * write, which `handleSave` performs before it posts.
 *
 * ⚠️ THREE STATES, NOT TWO, AND CONFLATING ANY OF THEM IS SILENT DATA LOSS ON A FILING SCREEN.
 *
 *   1. NOTHING CHANGED                      → do nothing.
 *   2. OUR OWN OPTIMISTIC WRITE CAME BACK   → consume it, keep the draft and the baseline.
 *   3. GENUINELY DIFFERENT VALUES ARRIVED   → re-seed.
 *
 * Distinguishing (2) from (3) BY VALUE rather than by object identity is the whole point, and both
 * of the obvious identity-based shortcuts are wrong in opposite directions:
 *
 *   * Advancing a "last seen" marker while a save is in flight swallows a genuine refresh that lands
 *     mid-save: it is recorded as consumed without ever reaching the draft, and once the save
 *     finishes nothing is left to act on, so the panel shows pre-refresh values for the rest of its
 *     life.
 *   * NOT advancing it means the optimistic write we made ourselves still looks new after the save
 *     finishes — so a FAILED save re-baselines the panel onto the very edits that never persisted,
 *     `hasChanges` goes false, and "Save Corrections" is disabled on a correction that was lost.
 *     That is the exact failure the save-correctness work exists to prevent.
 *
 * The row after our optimistic write holds precisely the values we wrote, so comparing values (and
 * the field list — see `isOurEcho`) tells the two apart.
 *
 * ACCEPTED RESIDUAL: a genuine server refresh that lands with EXACTLY our values and exactly our
 * fields is indistinguishable from our echo and is recorded rather than re-seeded. That costs nothing
 * observable — re-seeding from identical values would set the same draft and the same baseline — and
 * the alternative is a server-supplied write token this endpoint does not return. Field metadata
 * changes that preserve every value and every key would be missed; nothing on this panel renders from
 * such metadata today.
 */

export interface PanelSeed {
    rowId: number;
    /** Identity only — a cheap first check; value comparison decides the ambiguous case. */
    fields: unknown;
    coverageCode: unknown;
}

export interface PanelSeedSource {
    extractedDocumentDataId: number;
    fields: unknown;
    coverageCode: unknown;
}

/** What we last optimistically pushed into the row, by value. `null` when no save is pending. */
export interface OptimisticWrite {
    fields: Record<string, string>;
    coverageCode: unknown;
}

export type PanelReseedAction =
    /** Leave the draft, the baseline and the recorded marker exactly as they are. */
    | { action: 'none' }
    /** Our own echo: record it as seen so it cannot re-trigger, but do NOT touch draft or baseline. */
    | { action: 'record'; seed: PanelSeed }
    /** Real server values: adopt them into draft and baseline, and record them. */
    | { action: 'reseed'; seed: PanelSeed };

function valuesOf(fields: unknown): Record<string, string> {
    if (!Array.isArray(fields)) return {};
    return Object.fromEntries(
        fields.map((f: any) => [String(f?.key), f?.value == null ? '' : String(f.value)]),
    );
}

/** True when the row holds exactly the values we optimistically wrote — i.e. it is our own echo. */
function isOurEcho(row: PanelSeedSource, optimistic: OptimisticWrite | null): boolean {
    if (!optimistic) return false;
    if ((optimistic.coverageCode ?? '') !== (row.coverageCode ?? '')) return false;
    const rowValues = valuesOf(row.fields);
    const rowKeys = Object.keys(rowValues);
    const ourKeys = Object.keys(optimistic.fields);

    // ⚠️ THE KEY SET IS PART OF THE COMPARISON, NOT JUST THE VALUES. `valuesOf` normalises a missing
    // field, a null and an empty string to the same thing — which is right for deciding whether a
    // VALUE changed, and wrong for deciding whether the field LIST changed. Without this check a
    // server refresh that adds or removes an empty field reads as our own echo, so the panel never
    // re-seeds and never renders the field the server just added. Presence is a real difference: the
    // panel renders one input per field, so a field it does not hold is a field the filer cannot edit.
    if (rowKeys.length !== ourKeys.length) return false;
    for (const k of ourKeys) {
        if (!Object.prototype.hasOwnProperty.call(rowValues, k)) return false;
    }

    for (const k of rowKeys) {
        if ((rowValues[k] ?? '') !== (optimistic.fields[k] ?? '')) return false;
    }
    return true;
}

export function nextPanelSeed(
    seeded: PanelSeed,
    row: PanelSeedSource,
    saving: boolean,
    optimistic: OptimisticWrite | null = null,
): PanelReseedAction {
    const changed = seeded.rowId !== row.extractedDocumentDataId
        || seeded.fields !== row.fields
        // Coverage can change on its own: a reload revising only the coverage code while reusing the
        // same `fields` identity would otherwise leave draft and baseline holding the old one.
        || seeded.coverageCode !== row.coverageCode;
    if (!changed) return { action: 'none' };

    const seed: PanelSeed = {
        rowId: row.extractedDocumentDataId,
        fields: row.fields,
        coverageCode: row.coverageCode,
    };

    // Still in flight: decide nothing yet, and crucially do not record. Whatever this is — our echo or
    // a real refresh — it is re-evaluated on a later render once the save has settled.
    if (saving) return { action: 'none' };

    if (isOurEcho(row, optimistic)) return { action: 'record', seed };

    return { action: 'reseed', seed };
}
